// src/components/SearchCips/SearchCips.jsx

import React, {
  useState,
  useContext,
  useEffect,
  Fragment,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Transition } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import debounce from "lodash.debounce";
import { ProductsContext } from "../../context/products.context";
import useWindowSize from "../../hooks/useWindowSize";

// Importing the separated components
import CategoryToggle from "./CategoryToggle";
import SearchInput from "./SearchInput";
import ToggleSwitch from "./ToggleSwitch";
import FilterList from "./FilterList";

export default function SearchCips() {
  const searchInputRef = useRef(null);
  const size = useWindowSize();
  const isMobile = size.width < 1280; // Adjust based on your Tailwind breakpoint

  const {
    filterSession,
    setFilterSession,
    setCurrentCip,
    currentCip,
    mainCategory,
    setMainCategory,
    init,
  } = useContext(ProductsContext);

  const [enabled, setEnabled] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Derive initial area from currentCip when init is already cached (navigation-back case)
  const initialArea = (() => {
    if (currentCip && init?.cipMenu) {
      const cipItem = init.cipMenu.find((cip) => cip.cipCode === currentCip);
      if (cipItem) {
        return mainCategory === "Program" ? cipItem.programArea : cipItem.clusterName;
      }
    }
    return filterSession && filterSession !== "all" ? filterSession : null;
  })();

  const [fixedFilterSession, setFixedFilterSession] = useState(initialArea || filterSession);
  const [expandedAreas, setExpandedAreas] = useState(() =>
    initialArea ? new Set([initialArea]) : new Set()
  ); // Local UI state - which accordions are open

  const [query, setQuery] = useState("");
  const projects = useMemo(() => (init ? init.cipMenu : []), [init]);

  const searchRef = useRef();
  const lastExpandedBeforeSearch = useRef(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [inputValue, setInputValue] = useState("");

  // Debounced function to update the query state after 300ms
  const debouncedSetQuery = useMemo(
    () =>
      debounce((value) => {
        setQuery(value);
        setShowSuggestions(value.length > 0);
        setActiveIndex(-1);
      }, 300),
    []
  );

  // Handle change in the input field
  const handleChange = useCallback(
    (e) => {
      setInputValue(e.target.value);
      debouncedSetQuery(e.target.value);
    },
    [debouncedSetQuery]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  // Filter logic
  const filteredProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];

    return projects.filter((document) => {
      switch (fixedFilterSession) {
        case "all":
          return true;
        default:
          return enabled
            ? mainCategory === "Program"
              ? document.offered && document.programArea === fixedFilterSession
              : document.offered && document.clusterName === fixedFilterSession
            : mainCategory === "Program"
            ? document.programArea === fixedFilterSession
            : document.clusterName === fixedFilterSession;
      }
    });
  }, [projects, fixedFilterSession, enabled, mainCategory]);

  // Further filter based on query with a limit
  const filteredPeople = useMemo(() => {
    if (query === "") {
      return filteredProjects; // Return all filtered projects when no query
    }

    // Filtering logic when a query is present
    const lowerCaseQuery = query.toLowerCase().replace(/\s+/g, "");

    return filteredProjects.filter(
      (project) =>
        project.cipCode.toLowerCase().replace(/\s+/g, "").includes(lowerCaseQuery) ||
        project.cipCode
          .toString()
          .replace(/\B(?=(\d{4})+(?!\d))/g, ".")
          .replace(/\s+/g, "")
          .includes(lowerCaseQuery) ||
        project.cipcodeName.toLowerCase().replace(/\s+/g, "").includes(lowerCaseQuery)
    );
  }, [filteredProjects, query]);

  // Group CIP codes by category using useMemo
  const groupedPeople = useMemo(() => {
    if (!filteredPeople || filteredPeople.length === 0) return {};

    return filteredPeople.reduce((groups, project) => {
      const category =
        mainCategory === "Program" ? project.programArea : project.clusterName;

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(project);
      return groups;
    }, {});
  }, [filteredPeople, mainCategory]);

  const filterList = useMemo(() => {
    const preFilterListPrograms = new Set();

    projects.forEach((doc) => {
      const category = mainCategory === "Program" ? doc.programArea : doc.clusterName;
      preFilterListPrograms.add(category);
    });

    return ["all", ...preFilterListPrograms];
  }, [projects, mainCategory]);

  // Handle filter clicks - toggle accordion expansion (local UI state)
  const handleClick = useCallback(
    (area) => {
      if (query) {
        // When searching, just toggle this area's expansion
        setExpandedAreas((prev) => {
          const next = new Set(prev);
          if (next.has(area)) next.delete(area);
          else next.add(area);
          return next;
        });
      } else {
        // When not searching, single-expand + update filter
        const isExpanded = expandedAreas.has(area);
        if (isExpanded) {
          setExpandedAreas(new Set());
          setFixedFilterSession("all");
          setFilterSession("all");
        } else {
          setExpandedAreas(new Set([area]));
          setFixedFilterSession(area);
          setFilterSession(area);
        }
        setInputValue("");
        setQuery("");
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    },
    [query, expandedAreas, setFilterSession]
  );

  // Update fixedFilterSession based on query
  useEffect(() => {
    if (query.length > 0 && fixedFilterSession !== "all") {
      setFixedFilterSession("all");
    } else if (query.length === 0 && fixedFilterSession !== filterSession) {
      setFixedFilterSession(filterSession);
    }
  }, [query, filterSession, fixedFilterSession]);

  // Auto-expand areas with results when searching (borrowed from PathwaySidebarCustom)
  useEffect(() => {
    if (query) {
      // Save pre-search expansion state on first search character
      if (!lastExpandedBeforeSearch.current) {
        lastExpandedBeforeSearch.current = new Set(expandedAreas);
      }
      const areasWithResults = new Set(Object.keys(groupedPeople));
      setExpandedAreas(areasWithResults);
    } else if (lastExpandedBeforeSearch.current) {
      // Only restore when transitioning from search → no search
      setExpandedAreas(lastExpandedBeforeSearch.current.size > 0
        ? new Set(lastExpandedBeforeSearch.current)
        : new Set());
      lastExpandedBeforeSearch.current = null;
    }
    // When not searching and not restoring, do nothing — let handleClick manage expansion
  }, [query, groupedPeople]);

  // Handle category toggle (Program ↔ Cluster) in one event handler so React
  // batches mainCategory + filterSession together, preventing the context
  // validation effect from seeing a stale filterSession and overriding it.
  const handleCategoryChange = useCallback(
    (newCategory) => {
      setMainCategory(newCategory);

      // Find the correct area for the current CIP in the new category
      let areaName = null;
      if (currentCip && projects.length > 0) {
        const cipItem = projects.find((cip) => cip.cipCode === currentCip);
        if (cipItem) {
          areaName = newCategory === "Program" ? cipItem.programArea : cipItem.clusterName;
        }
      }

      setExpandedAreas(areaName ? new Set([areaName]) : new Set());
      setFixedFilterSession(areaName || "all");
      setFilterSession(areaName || "all");
      setInputValue("");
      setQuery("");
      setShowSuggestions(false);
      setActiveIndex(-1);
    },
    [currentCip, projects, setMainCategory, setFilterSession]
  );

  // Auto-expand the area for the current CIP on mount / CIP change (from PathwaySidebarCustom)
  useEffect(() => {
    if (currentCip && !query && projects.length > 0) {
      const cipItem = projects.find((cip) => cip.cipCode === currentCip);
      if (cipItem) {
        const areaName = mainCategory === "Program" ? cipItem.programArea : cipItem.clusterName;
        if (areaName && !expandedAreas.has(areaName)) {
          setExpandedAreas(new Set([areaName]));
          setFixedFilterSession(areaName);
          setFilterSession(areaName);
        }
      }
    }
  }, [currentCip, projects, mainCategory, query]);

  // Handle clicks outside the search suggestions to close them
  useEffect(() => {
    if (!showSuggestions) return; // If suggestions are not shown, do nothing

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  // Flatten groupedPeople for keyboard navigation
  const flatCips = useMemo(() => {
    return Object.values(groupedPeople)
      .flat()
      .map((cip, idx) => ({ ...cip, index: idx }));
  }, [groupedPeople]);

  // Stable index lookup so render doesn't rely on mutable counter
  const indexByCip = useMemo(() => {
    const map = new Map();
    flatCips.forEach((c, i) => map.set(c.cipCode, i));
    return map;
  }, [flatCips]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestions) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev < flatCips.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        setActiveIndex(-1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < flatCips.length) {
          const selected = flatCips[activeIndex];
          setCurrentCip(selected.cipCode);
          setShowSuggestions(false);
          setActiveIndex(-1);
          setInputValue(""); // Clear the search input
          setQuery(""); // Clear the query
        }
      }
    },
    [showSuggestions, flatCips, activeIndex, setCurrentCip]
  );

  // Scroll into view when activeIndex changes
  useEffect(() => {
    if (activeIndex >= 0) {
      const element = document.getElementById(`suggestion-${activeIndex}`);
      if (element) {
        element.scrollIntoView({
          behavior: "auto",
          block: "nearest",
        });
      }
    }
  }, [activeIndex]);

  return (
    <div className="relative select-none">
      {isMobile && (
        <>
          {/* Mobile Header */}
          <div className="flex items-center mt-[67px] p-4 bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30 ">
            {/* Placeholder for potential mobile header content */}
            <div></div>
            {/* Sidebar Toggle Button */}
            <button
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open sidebar"
              aria-expanded={isSidebarOpen}
            >
              <div className="h-6 w-6 text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </div>
            </button>

            {/* Search Input */}
            <div className="flex-1 ml-4 relative" ref={searchRef}>
              <SearchInput
                query={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (inputValue.length > 0) setShowSuggestions(true);
                }}
                placeholder="Search..."
                id="sidebarCipSearch"
                inputRef={searchInputRef}
              />

              {/* Search Suggestions Dropdown */}
              <AnimatePresence>
                {showSuggestions && query.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-zinc-300 rounded-md shadow-lg mt-1 max-h-72 overflow-y-auto z-40">
                    {filteredPeople && filteredPeople.length > 0 ? (
                      Object.entries(groupedPeople).map(([category, cips]) => (
                        <div key={category}>
                          {/* Category Header */}
                          <div className="px-4 py-2 bg-zinc-100 font-semibold text-zinc-700 flex justify-between items-center">
                            {category}
                            <div className="h-4 w-4 text-zinc-500">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-6"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            </div>
                          </div>
                          {/* CIP Codes under Category */}
                          {cips.map((project) => {
                            const idx = indexByCip.get(project.cipCode);
                            return (
                              <button
                                key={project.cipCode}
                                id={`suggestion-${idx}`}
                                onClick={() => {
                                  setCurrentCip(project.cipCode);
                                  setShowSuggestions(false);
                                  setActiveIndex(-1);
                                  setInputValue('');
                                  setQuery('');
                                }}
                                className={`w-full text-left px-4 py-2 flex items-start focus:outline-none border-l ${
                                  project.offered
                                    ? 'border-violet-500'
                                    : 'border-gray-300'
                                } ${
                                  idx === activeIndex ? 'bg-zinc-200' : ''
                                } hover:bg-zinc-100 min-w-0`}
                              >
                                <div className="flex gap-2 items-start w-full min-w-0">
                                  <span className="tabular-nums text-xs font-light whitespace-nowrap">
                                    {project.cipCode}
                                  </span>
                                  <span className="mx-1 whitespace-nowrap">-</span>
                                  <span className="flex-1 break-words min-w-0">
                                    {project.cipcodeName}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-zinc-500">
                        No results found.
                      </div>
                    )}
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Sidebar */}
          <Transition show={isSidebarOpen} as={Fragment}>
            <div className="fixed inset-0 z-40 xl:hidden">
              {/* Overlay */}
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-50"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-50"
                leaveTo="opacity-0"
              >
                <div
                  className="absolute inset-0 bg-black opacity-50"
                  onClick={() => setIsSidebarOpen(false)}
                  aria-hidden="true"
                ></div>
              </Transition.Child>

              {/* Sidebar Panel */}
              <Transition.Child
                as={Fragment}
                enter="transition-transform ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition-transform ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <div className="relative bg-white w-[22.5rem] max-w-full h-full shadow-xl overflow-y-auto">
                  {/* Close Button */}
                  <button
                    className="absolute top-4 right-4 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Close sidebar"
                  >
                    <div className="h-6 w-6 text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Sidebar Content */}
                  <div className="mt-16 px-4">
                    <CategoryToggle
                      mainCategory={mainCategory}
                      setMainCategory={handleCategoryChange}
                    />

                    <SearchInput
                      query={inputValue}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (inputValue.length > 0) setShowSuggestions(true);
                      }}
                      placeholder="Search..."
                      id="sidebarCipSearch"
                    />

                    <ToggleSwitch
                      enabled={enabled}
                      setEnabled={setEnabled}
                      districtNameShort={init && init.districtNameShort}
                    />

                    <FilterList
                      filterList={filterList}
                      expandedAreas={expandedAreas}
                      handleClick={handleClick}
                      groupedPeople={groupedPeople}
                      setCurrentCip={setCurrentCip}
                      currentCip={currentCip}
                      setIsSidebarOpen={setIsSidebarOpen}
                      query={query}
                    />
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Transition>
        </>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="hidden md:block top-0 left-0 bottom-0 bg-white z-40 overflow-y-auto mt-16 pb-10 transition-all duration-300">
          <CategoryToggle
            mainCategory={mainCategory}
            setMainCategory={handleCategoryChange}
          />

          <div className="sticky top-0 z-40 h-[100px] pt-4 bg-white">
            <div className="ml-4 mt-5 px-8 relative">
              <SearchInput
                query={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (inputValue.length > 0) setShowSuggestions(true);
                }}
                placeholder="Search..."
                id="sidebarCipSearch"
              />
            </div>
          </div>

          <div className="flex z-50 justify-end flex-row pb-4 gap-4 text-sm font-light text-zinc-600 px-4">
            <ToggleSwitch
              enabled={enabled}
              setEnabled={setEnabled}
              districtNameShort={init && init.districtNameShort}
            />
          </div>

          <FilterList
            filterList={filterList}
            expandedAreas={expandedAreas}
            handleClick={handleClick}
            groupedPeople={groupedPeople}
            setCurrentCip={setCurrentCip}
            currentCip={currentCip}
            setIsSidebarOpen={setIsSidebarOpen}
            query={query}
          />
        </div>
      )}
    </div>
  );
};
